import React, { useState } from 'react';
import { Button, Photo, Pill, SkeletonLoader, TextareaReadOnly, textareaInitialState } from '../';
import { useTooltip, useGlobalState } from '../../hooks';
import { ASSET_HIERARCHY_MAP, renderUrl, isLinkableUrl } from '../../helpers';

import './ObjectDetails.css';

export const ObjectDetails = ({
  title,
  statusId,
  photo,
  secondaryTitle,
  subtitleIcon,
  subtitleText,
  description,
  details,
  fromReferences,
  showDescription = true,
  type,
  loading,
  editable,
  buttons,
  openLink,
  children,
}) => {
  const [showDetails, setShowDetails] = useState(
    localStorage.getItem('showDetails') === 'true' ? true : false
  );
  const [settings] = useGlobalState('settings');
  const [statuses] = useGlobalState('statuses');

  const { showTooltip, hideTooltip } = useTooltip();

  const toggleDetails = () => {
    const state = !showDetails;

    setShowDetails(state);

    // Persist state in local storage
    localStorage.setItem('showDetails', state.toString());
  };

  const renderSubtitleIcon = (icon, index) => {
    if (!icon) {
      return null;
    }
    return (
      <div className="object-subtitle-icon" key={index}>
        {!loading ? icon : <SkeletonLoader circle={true} width="1.2em" height="1.2em" />}
      </div>
    );
  };

  const isUser = type === 'USER' || type === 'USER_GROUP';

  return (
    <div className={'ObjectDetails' + (isUser ? ' center' : '')}>
      {!isUser && (
        <div className="object-type">
          {type}
          {statusId &&
            settings &&
            settings[`SHOW_${ASSET_HIERARCHY_MAP[type.toLowerCase()]}_STATUSES`.toUpperCase()] && (
              <Pill label={statusId} values={statuses} />
            )}
        </div>
      )}

      {isUser && <Photo type={type} size="large" data={photo} name={title} loading={loading} />}

      <div className="object-title">
        <h1>{title || <SkeletonLoader width="10vw" />}</h1>
      </div>

      {secondaryTitle && <div className="object-secondary-title">{secondaryTitle}</div>}

      {(subtitleIcon || subtitleText) && (
        <div className="object-subtitle">
          {subtitleIcon && Array.isArray(subtitleIcon)
            ? subtitleIcon.map((icon, index) => {
                return renderSubtitleIcon(icon, index);
              })
            : renderSubtitleIcon(subtitleIcon)}
          {subtitleText && (
            <div className="object-subtitle-text">
              {!loading ? (
                subtitleText
              ) : (
                <React.Fragment>
                  &nbsp;&nbsp;&nbsp;&nbsp; <SkeletonLoader width="10vw" />
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      )}

      <div className="object-container">
        {showDescription && (
          <div className="object-description">
            {!loading ? (
              !description ? (
                <span className="no-result-text">No description available</span>
              ) : (
                <TextareaReadOnly
                  value={textareaInitialState({
                    value: description.trim().replace(/[\n\r]{2,}/g, '\n'),
                    readOnly: true,
                    descriptions: fromReferences,
                  })}
                  maxLines={20}
                />
              )
            ) : (
              <SkeletonLoader width="10vw" count={2} />
            )}
          </div>
        )}

        {/* Directly under the description, where the eye already is -- not in the toolbar with EDIT
            and DELETE. Opening an asset in its source system is something a reader does too, and
            the toolbar is only shown to those who may edit. */}
        {openLink && !loading && (
          <div className="object-source-link">
            <Button
              value={openLink.label}
              /*icon="external_link"*/
              tooltip={openLink.tooltip || openLink.href}
              onClick={() => window.open(openLink.href, '_blank', 'noopener,noreferrer')}
            />
          </div>
        )}

        <div className="object-content">{children}</div>

        {details && details.filter(detail => detail.data).length > 0 && !loading && (
          <React.Fragment>
            <div
              className={'object-details-toggle-button' + (showDetails ? ' show' : '')}
              onClick={toggleDetails}
              onMouseEnter={() =>
                showTooltip({ title: showDetails ? 'Show details' : 'Hide details' })
              }
              onMouseLeave={hideTooltip}
            >
              Details
              <span></span>
            </div>

            <div className={'object-details' + (showDetails ? ' show' : '')}>
              <table>
                <tbody>
                  {details.map((detail, index) => {
                    if (detail.data) {
                      return (
                        <tr key={index}>
                          <td
                            className="object-details-title"
                            onMouseEnter={() =>
                              detail.tooltip && showTooltip({ title: detail.tooltip })
                            }
                            onMouseLeave={hideTooltip}
                          >
                            {detail.title}
                          </td>
                          <td className="object-details-data">
                            {/* A detail flagged as a URL is only linked when it actually is one.
                                Columns like dataset_source_url hold "a URL or a path" and the
                                connector decides which, so a storage path shows as text rather
                                than as a link to nowhere. */}
                            {detail.isUrl && isLinkableUrl(detail.data) ? (
                              <a href={renderUrl(detail.data)} target="_blank" rel="noreferrer">
                                {detail.data}
                              </a>
                            ) : (
                              detail.data
                            )}
                          </td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            </div>
          </React.Fragment>
        )}
      </div>

      {!loading && editable && buttons && (
        <div className="main-toolbar">
          {buttons.map(
            (button, index) =>
              button && (
                <Button
                  key={index}
                  value={button.value}
                  buttonStyle={button.buttonStyle}
                  className="main-toolbar-item"
                  onClick={button.onClick}
                  disabled={button.disabled}
                  tooltip={button.tooltip}
                />
              )
          )}
        </div>
      )}
    </div>
  );
};
